// BarraAccesoV4.jsx
//
// #1439 B (mockup aprobado tools/mockups/smartdx_mockups_menu_crear_cuenta_v4.html): en las
// pantallas de acceso (/authentication, /recuperar-clave, /nueva-clave) va la MISMA barra de la
// home v4, en su versión cápsula oscura (la que la home muestra al hacer scroll), en vez de la
// píldora de la DxNavbar. Al tocar "Crear cuenta gratis" en la home, la barra de arriba ya no
// cambia de forma.
//
// - Escritorio: links a las secciones de la home, logo al centro y "Volver al inicio". No lleva
//   Ingresar / Crear cuenta: las pestañas del formulario hacen eso.
// - Celular: logo y el botón de 9 puntos, que abre un menú con las mismas secciones.
// El responsive va por CSS (`display` por breakpoint), no por useMediaQuery: así no hay un
// primer render con la barra equivocada.
// Siempre oscura (el acceso lo es): vive dentro de AlcanceOscuro, todos los colores por token.
import React, { useEffect, useRef, useState } from "react";
import { Box } from "@mui/material";
import { useNavigate } from "react-router-dom";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import CloseIcon from "@mui/icons-material/Close";

import AlcanceOscuro from "../AlcanceOscuro";

// Ids de las secciones de public/home_v4.html (los mismos links de su barra).
export const SECCIONES_HOME_V4 = [
  ["Modo Turno", "como-funciona"],
  ["Informes", "informes"],
  ["Equipo", "equipo"],
  ["Conversemos", "conversemos"],
];

// Logo de la app en SVG, del color del texto. Es el mismo grafo que public/icono_smartdx.svg (maestro
// del ícono, hub sólido) recortado al glifo: si cambia uno, cambian los dos y home_v4.html (#1553).
const Logo = () => (
  <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: "9px", fontWeight: 600, fontSize: 22, letterSpacing: "-0.03em" }}>
    <Box component="svg" viewBox="20 16 59.5 70.5" aria-hidden="true" sx={{ width: 21, height: 25, fill: "currentColor", flex: "none" }}>
      <path d="M48 47L36 34M48 47L64 31M48 47L66 47M48 47L34 56M48 47L58 62" stroke="currentColor" strokeWidth="3.2" strokeLinecap="round" fill="none" />
      <circle cx="48" cy="47" r="4.8" />
      <circle cx="36" cy="31" r="8.5" />
      <circle cx="66" cy="27" r="11" />
      <circle cx="67" cy="47" r="4.8" />
      <circle cx="30" cy="57" r="10" />
      <circle cx="64" cy="71" r="15.5" />
    </Box>
    <span>
      Smart<Box component="span" sx={{ color: "var(--color-primary-soft)" }}>Dx</Box>
    </span>
  </Box>
);

const linkSx = {
  display: "inline-flex", alignItems: "center", minHeight: 44, fontSize: 14, fontWeight: 500,
  color: "var(--text-primary)", textDecoration: "none", opacity: 0.82, transition: "opacity .2s",
  "&:hover, &:focus-visible": { opacity: 1 },
  "&:focus-visible": { outline: "2px solid var(--color-primary-soft)", outlineOffset: 3, borderRadius: "6px" },
};

export default function BarraAccesoV4() {
  const navigate = useNavigate();
  const [menu, setMenu] = useState(false);
  const botonMenu = useRef(null);
  const botonCerrar = useRef(null);

  // Con el menú del celular abierto la página de atrás no scrollea, y Escape lo cierra. El foco
  // entra al menú (es un diálogo modal) y al cerrar vuelve al botón que lo abrió: antes se quedaba
  // en "Abrir menú", detrás del diálogo (revisor #1439 r2).
  useEffect(() => {
    if (!menu) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    botonCerrar.current?.focus();
    const onKey = (e) => { if (e.key === "Escape") setMenu(false); };
    window.addEventListener("keydown", onKey);
    const boton = botonMenu.current;
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
      if (boton && boton.isConnected) boton.focus();
    };
  }, [menu]);

  const ir = (e, hash) => {
    e.preventDefault();
    setMenu(false);
    navigate(hash ? { pathname: "/", hash: `#${hash}` } : "/");
  };

  return (
    <AlcanceOscuro sx={{ display: "contents" }}>
      <Box
        component="header"
        data-testid="barra-acceso-v4"
        sx={{
          position: "fixed", zIndex: 1100, left: "50%", transform: "translateX(-50%)",
          top: "max(12px, env(safe-area-inset-top))",
          width: "min(1040px, calc(100% - 24px))", height: 54, borderRadius: "999px",
          display: "grid", alignItems: "center", gap: "20px",
          gridTemplateColumns: { xs: "auto 1fr", md: "1fr auto 1fr" },
          pl: { xs: "18px", md: "24px" }, pr: { xs: "6px", md: "8px" },
          color: "var(--text-primary)",
          backgroundColor: "rgba(var(--bg-elevated-rgb),0.72)",
          border: "1px solid rgba(var(--fg-rgb),0.12)",
          backdropFilter: "blur(16px) saturate(140%)", WebkitBackdropFilter: "blur(16px) saturate(140%)",
          boxShadow: "0 18px 40px -20px rgba(var(--shadow-rgb),0.6)",
        }}
      >
        <Box component="nav" aria-label="Secciones de la home" sx={{ display: { xs: "none", md: "flex" }, gap: "26px" }}>
          {SECCIONES_HOME_V4.map(([texto, id]) => (
            <Box key={id} component="a" href={`/#${id}`} onClick={(e) => ir(e, id)} sx={linkSx}>{texto}</Box>
          ))}
        </Box>
        <Box component="a" href="/" onClick={(e) => ir(e)} aria-label="SmartDx, volver al inicio" sx={{ ...linkSx, opacity: 1, justifySelf: { xs: "start", md: "center" } }}>
          <Logo />
        </Box>
        <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center" }}>
          <Box
            component="a"
            href="/"
            onClick={(e) => ir(e)}
            data-testid="barra-acceso-volver"
            sx={{ ...linkSx, display: { xs: "none", md: "inline-flex" }, gap: "6px", px: "14px", borderRadius: "999px", "&:hover": { opacity: 1, backgroundColor: "rgba(var(--fg-rgb),0.08)" } }}
          >
            <HomeOutlinedIcon sx={{ fontSize: 18 }} />Volver al inicio
          </Box>
          <Box
            component="button"
            type="button"
            aria-label="Abrir menú"
            aria-expanded={menu}
            aria-controls="barra-acceso-menu"
            data-testid="barra-acceso-menu-btn"
            ref={botonMenu}
            onClick={() => setMenu(true)}
            sx={{
              display: { xs: "grid", md: "none" }, placeItems: "center", width: 44, height: 44, borderRadius: "50%",
              border: 0, backgroundColor: "transparent", color: "var(--text-primary)", cursor: "pointer",
              "&:focus-visible": { outline: "2px solid var(--color-primary-soft)", outlineOffset: 2 },
            }}
          >
            <Box component="span" aria-hidden="true" sx={{ display: "grid", gridTemplateColumns: "repeat(3, 4px)", gap: "4px", "& i": { width: 4, height: 4, borderRadius: "50%", backgroundColor: "currentColor" } }}>
              {Array.from({ length: 9 }, (_, i) => <i key={i} />)}
            </Box>
          </Box>
        </Box>
      </Box>

      {menu && (
        <Box
          id="barra-acceso-menu"
          role="dialog"
          aria-modal="true"
          aria-label="Menú"
          data-testid="barra-acceso-menu"
          sx={{
            position: "fixed", inset: 0, zIndex: 1600, display: "flex", flexDirection: "column", gap: "6px",
            p: "calc(84px + env(safe-area-inset-top)) 20px 32px", color: "var(--text-primary)",
            backgroundColor: "rgba(var(--bg-elevated-rgb),0.97)", backdropFilter: "blur(10px)", WebkitBackdropFilter: "blur(10px)",
          }}
        >
          <Box
            component="button"
            type="button"
            aria-label="Cerrar menú"
            ref={botonCerrar}
            onClick={() => setMenu(false)}
            sx={{
              position: "absolute", right: 20, top: "calc(18px + env(safe-area-inset-top))", width: 44, height: 44, borderRadius: "50%",
              display: "grid", placeItems: "center", cursor: "pointer", color: "var(--text-primary)",
              border: "1px solid rgba(var(--fg-rgb),0.16)", backgroundColor: "rgba(var(--fg-rgb),0.06)",
            }}
          >
            <CloseIcon fontSize="small" />
          </Box>
          {SECCIONES_HOME_V4.map(([texto, id]) => (
            <Box
              key={id}
              component="a"
              href={`/#${id}`}
              onClick={(e) => ir(e, id)}
              sx={{ fontSize: 30, fontWeight: 500, letterSpacing: "-0.03em", py: "10px", color: "var(--text-primary)", textDecoration: "none", borderBottom: "1px solid rgba(var(--fg-rgb),0.09)" }}
            >
              {texto}
            </Box>
          ))}
          <Box
            component="a"
            href="/"
            onClick={(e) => ir(e)}
            sx={{
              mt: "24px", display: "flex", alignItems: "center", justifyContent: "center", gap: 1, minHeight: 52, borderRadius: "999px",
              fontSize: 16, fontWeight: 500, color: "var(--text-primary)", textDecoration: "none", border: "1px solid rgba(var(--fg-rgb),0.2)",
            }}
          >
            <HomeOutlinedIcon sx={{ fontSize: 18 }} />Volver al inicio
          </Box>
        </Box>
      )}
    </AlcanceOscuro>
  );
}
